import { BTSelectItem } from "types/apiResponse/apiResponse";
import { type AccountingIntegrationType } from "types/enum";

import { getSelectedValue } from "utilities/form/form";
import { isNullOrUndefined } from "utilities/object/object";

import { CreateNewAccountingEntityID } from "commonComponents/entity/accounting/common/accountingConstants";

import { JobLinkingTypes } from "entity/accounting/LinkJobToAccounting/LinkJobToAccounting.api.types";

export type LinkEntityToAccountingFormActions = "cancel" | "saveAccounting" | undefined;

export interface ISelectAccountingEntityFormValues {
    accountingEntity: number;
}

export interface ILinkToAccountingProps {
    entity: LinkToAccountingEntity;
    onCancel: () => void;
}

export enum IDTypes {
    Unknown = 0,
    JobID = 1,
    VendorID = 2,
    InvoiceARAccountID = 4,
    InvoiceItemID = 5,
    CostCodeID = 8,
    BillAPAccountID = 7,
    EmployeeID = 14,
    // Matched name on server side. Used for both checks and credit card payment methods
    PaymentMethodBankAccount = 18,
    PayrollItemID = 34,
    CreditMemo = 43,
    CostTypeID = 51,
}

export class LinkToAccountingEntity {
    constructor(data: any) {
        // Only 1 of the following booleans can/should be true.
        const hasCustomerOptions: boolean = Boolean(data.customerOptions);
        const hasJobOptions: boolean = Boolean(data.jobOptions);
        const hasProjectOptions: boolean = Boolean(data.projectOptions);
        const isSelectLinkingResp: boolean = Boolean(data.linkToAccountingEntityText);

        let accountingEntity: string;

        switch (true) {
            case hasCustomerOptions:
                accountingEntity = `${
                    data.accountingIntegrationName
                } ${data.accountingTermForPrimaryJobEntity.toLowerCase()}`;

                const customerOptions = data.customerOptions.value
                    .filter((cust: any) => cust.id !== CreateNewAccountingEntityID.toString())
                    .map((cust: any) => new BTSelectItem(cust));

                this.accountingType = data.accountingType.value;
                this.buttonText = `Create new ${accountingEntity}`;
                this.createNewAccountingCustomerEnabled = data.createNewAccountingCustomerEnabled;
                this.createNewDescriptionText = data.createNewCustomerDescriptionText;
                this.hasNotSelectedAccountingCustomerText =
                    data.hasNotSelectedAccountingCustomerText;
                this.disclaimer = `* Changing the ${data.accountingTermForPrimaryJobEntity.toLowerCase()} link will reset the job link because in ${
                    data.accountingIntegrationName
                }, a job is associated with a ${data.accountingTermForPrimaryJobEntity.toLowerCase()}.`;
                this.dropDownLabel = accountingEntity;
                this.hasError = data.hasError;
                this.integrationName = data.accountingIntegrationName;
                this.options = customerOptions;
                this.titleVerbiage = `Select a ${accountingEntity} to link with this job`;
                this.type = JobLinkingTypes.Customer;
                this.value = getSelectedValue(customerOptions);
                if (data.accountingCustomerOptions) {
                    const accountingCustomerOptions = data.accountingCustomerOptions.value.map(
                        (contact: any) =>
                            new BTSelectItem(
                                contact,
                                (contact) => (contact.value = contact.value.toString()),
                            ),
                    );
                    this.accountingCustomerOptions = accountingCustomerOptions;
                    this.accountingContactValue =
                        getSelectedValue(accountingCustomerOptions) ||
                        data.accountingCustomerOptions.unselectedId.toString();
                }

                break;
            case hasJobOptions:
                accountingEntity = `${data.accountingIntegrationName} job`;

                const jobOptions = data.jobOptions.value.map((job: any) => new BTSelectItem(job));

                this.accountingType = data.accountingType.value;
                this.buttonText = `Create new ${accountingEntity}`;
                this.createNewDescriptionText = `if this job does not already exist in ${data.accountingIntegrationName}.`;
                this.dropDownLabel = accountingEntity;
                this.integrationName = data.accountingIntegrationName;
                this.options = jobOptions;
                this.titleVerbiage = `Select a ${accountingEntity} to link with this job`;
                this.type = JobLinkingTypes.Job;
                this.value = getSelectedValue(jobOptions);

                break;
            case hasProjectOptions:
                accountingEntity = `${data.accountingIntegrationName} project`;

                const projectOptions = data.projectOptions.value.map(
                    (project: unknown) => new BTSelectItem(project),
                );

                this.accountingType = data.accountingType.value;
                this.buttonText = `Create new ${accountingEntity}`;
                this.createNewDescriptionText = `if this project does not already exist in ${data.accountingIntegrationName}.`;
                this.dropDownLabel = accountingEntity;
                this.integrationName = data.accountingIntegrationName;
                this.options = projectOptions;
                this.titleVerbiage = `Select a ${accountingEntity} to link with this job`;
                this.type = JobLinkingTypes.Project;
                this.value = getSelectedValue(projectOptions);

                break;
            case isSelectLinkingResp:
                this.options = [
                    new BTSelectItem({
                        id: JobLinkingTypes.Customer,
                        selected: data.jobsiteLinkingType === JobLinkingTypes.Customer,
                        title: data.linkToAccountingEntityText,
                    }),
                    new BTSelectItem({
                        id: JobLinkingTypes.Job,
                        selected: data.jobsiteLinkingType === JobLinkingTypes.Job,
                        title: data.selectLinkToAccountingJobText,
                    }),
                ];
                if (!isNullOrUndefined(data.selectLinkToAccountingProjectText)) {
                    this.options.push(
                        new BTSelectItem({
                            id: JobLinkingTypes.Project,
                            selected: data.jobsiteLinkingType === JobLinkingTypes.Project,
                            title: data.selectLinkToAccountingProjectText,
                        }),
                    );
                }
                this.titleVerbiage = `Select a ${data.accountingIntegrationName} linking type for this job`;
                this.type = JobLinkingTypes.SelectAccountingLinkingType;

                break;
            default:
                this.buttonText = data.buttonText;
                this.createNewDescriptionText = data.createNewDescriptionText;
                this.defaultSelectedValue = data.defaultSelectedValue;
                this.disclaimer = data.disclaimer;
                this.dropDownLabel = data.dropDownLabel;
                this.integrationName = data.integrationName;
                this.options = data.options.map(
                    (opt: any) =>
                        new BTSelectItem({ id: opt.Value, name: opt.Text, selected: opt.Selected }),
                );
                this.titleVerbiage = data.titleVerbiage;
                this.value = data.value;
        }
    }

    accountingType?: AccountingIntegrationType;
    buttonText?: string;
    createNewAccountingCustomerEnabled?: boolean;
    createNewDescriptionText?: string;
    hasNotSelectedAccountingCustomerText?: string;
    defaultSelectedValue?: string;
    disclaimer?: string;
    dropDownLabel?: string;
    hasError?: boolean;
    integrationName?: string;
    options: BTSelectItem[];
    titleVerbiage: string;
    type?: JobLinkingTypes;
    value: string;
    accountingCustomerOptions?: BTSelectItem[];
    accountingContactValue?: string;
}

export class EntityOptions {
    constructor(data: any) {
        this.title = data.title;
        this.unselectedId = data.unselectedId;
        this.value = data.value.map((ent: any) => new BTSelectItem(ent));
    }

    title: string;
    unselectedId: number;
    value: BTSelectItem[];
}

export class LinkingOptions {
    constructor(data: any) {
        this.leftText = data.leftText;
        this.rightSelected = data.rightSelected;
        this.rightText = data.rightText;
    }

    leftText: string;
    rightSelected: boolean;
    rightText: string;
}

export class AccountingIntegrationTypes {
    constructor(data: any) {
        this.value = data.value;
    }

    value: number;
}

export interface ILinkEntityToAccountingGetRequest {
    idType: IDTypes;
    entityID?: number;
}

export interface ILinkEntityToAccountingPutRequest {
    accountingEntityID?: string;
    idType: IDTypes;
    numericEntityID: number;
}
